import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, X } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  useCaptureCountItem,
  useCloseCountSession,
  useCurrentCountSession,
  useResolveCountItem,
  useReviewCountSession,
  useSkipRemainingCountItems,
} from '../hooks/use-count-session'
import { useWakeLock } from '../hooks/use-wake-lock'
import { useCountAssortment } from '../hooks/use-count-assortment'
import { useCountRestaurantStore } from '../store/count-restaurant-store'
import { QuantitySheet } from '../components/quantity-sheet'
import { CountReviewSection } from '../components/count-review-section'
import { CountReasonPicker } from '../components/count-reason-picker'
import { CountVarianceRow } from '../components/count-variance-row'
import { CountNotFoundRow } from '../components/count-not-found-row'
import { CountItemSheet } from '../components/count-item-sheet'
import { CountSummaryBar } from '../components/count-summary-bar'
import { safeAreaBottom } from '../lib/safe-area'
import { newCaptureId } from '../lib/capture-flow'
import { bookedAmount } from '../lib/count-item-summary'
import type { CountItemSheetTarget } from '../components/count-item-sheet'
import type {
  AffectedOrder,
  CountItemView,
  CountReviewResult,
  CountVarianceReason,
} from '@/api/endpoints/inventory-count-sessions'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { ApiError } from '@/api/types'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

const HIGHLIGHT_MS = 2000

function isPending(item: CountItemView) {
  return item.resolution === 'PENDING'
}

/**
 * Whether this resolution applied a stock correction. Only the two confirms do — a variance of
 * zero and a confirm-zero on an already-empty article both resolve as MATCHED server-side, and
 * MATCHED and SKIPPED never write a movement.
 */
function movedStock(item: CountItemView) {
  return item.resolution === 'CONFIRMED' || item.resolution === 'CONFIRMED_ZERO'
}

/** What a quantity entered for this article means. A never-captured one has none to replace. */
type ItemKind = 'variance' | 'notCounted'

interface ConfirmArgs {
  qty?: number
  reason?: CountVarianceReason
  recountConfirmed?: boolean
}

export function CountReviewPage() {
  const { t } = useTranslation('inventory-count')
  const navigate = useNavigate()
  const restaurantId = useCountRestaurantStore((s) => s.restaurantId)
  const { data: current } = useCurrentCountSession(restaurantId)
  const sessionId = current?.session.id ?? null
  // "Teilmengen zählen" per article (CHG-2026-09-stock-usage-and-price-twins, D9): the recount pad
  // gets the comma only where the assortment says so. Same cache entry as the capture screen.
  const assortment = useCountAssortment(restaurantId, sessionId)

  const review = useReviewCountSession(restaurantId, sessionId)
  const resolveItem = useResolveCountItem(restaurantId, sessionId)
  const capture = useCaptureCountItem(restaurantId, sessionId)
  const skipRemaining = useSkipRemainingCountItems(restaurantId, sessionId)
  const closeSession = useCloseCountSession(restaurantId, sessionId)

  // The review screen keeps the phone awake too: resolving twenty variances is slower than the
  // display timeout, and a locked screen mid-review costs the scroll position.
  useWakeLock(sessionId != null)

  const [data, setData] = useState<CountReviewResult | null>(null)
  // One reason for both variance sections, overridable per article in its sheet. Only the *asking*
  // is batched: every confirm still posts its own reason on its own per-item resolve call, so the
  // reason stays a property of the article and the report keeps meaning what it meant.
  const [sectionReason, setSectionReason] = useState<
    CountVarianceReason | undefined
  >(undefined)
  /** The five pills, before a reason is chosen and whenever the counter reopens the choice. */
  const [reasonPickerOpen, setReasonPickerOpen] = useState(true)
  const [sheetTarget, setSheetTarget] = useState<CountItemSheetTarget | null>(
    null,
  )
  /** The digit pad, opened out of the article sheet for a quantity that is not a step away. */
  const [padTarget, setPadTarget] = useState<{
    item: CountItemView
    kind: ItemKind
  } | null>(null)
  /** A resolved article whose decision the counter wants back. */
  const [reopenTarget, setReopenTarget] = useState<CountItemView | null>(null)
  const [pendingConfirm, setPendingConfirm] = useState<{
    item: CountItemView
    kind: ItemKind
    args: ConfirmArgs
    affectedOrders: Array<AffectedOrder>
  } | null>(null)
  const [busy, setBusy] = useState(false)

  // The item DOM refs live in component state across reloads (loadReview only replaces `data`, it
  // never remounts this component), so the scroll position survives resolving an item.
  const [highlightSku, setHighlightSku] = useState<string | null>(null)
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const itemNodes = useRef<Record<string, HTMLDivElement | null>>({})

  function registerItemNode(sku: string) {
    return (el: HTMLDivElement | null) => {
      itemNodes.current[sku] = el
    }
  }

  function loadReview() {
    if (!sessionId) return
    review.mutate(undefined, { onSuccess: setData })
  }

  useEffect(loadReview, [sessionId])

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current)
    },
    [],
  )

  function reportFailure(err: unknown) {
    if (err instanceof ApiError && err.status === 409) {
      toast.error(t('review.recountRequired'))
    } else {
      toast.error(t('review.actionFailed'))
    }
  }

  /**
   * Settles one article, quantity first.
   *
   * A changed quantity is a `recount` on a captured article and a `set` capture on one that was
   * never counted; only then does the confirm run, so the correction is computed from the number
   * the counter just agreed to. A never-captured article left at 0 skips both and takes the
   * `confirm_zero` path, which needs no reason — the same answer the old "Wirklich 0" button gave.
   */
  async function settleItem(
    item: CountItemView,
    kind: ItemKind,
    args: ConfirmArgs,
  ) {
    setBusy(true)
    try {
      const zero = kind === 'notCounted' && (args.qty ?? 0) === 0
      if (zero) {
        await resolveItem.mutateAsync({ sku: item.sku, action: 'confirm_zero' })
      } else {
        if (args.qty != null) {
          if (item.countedQty == null) {
            await capture.mutateAsync({
              sku: item.sku,
              qty: args.qty,
              mode: 'set',
              captureId: newCaptureId(),
            })
          } else if (args.qty !== item.countedQty) {
            await resolveItem.mutateAsync({
              sku: item.sku,
              action: 'recount',
              recountQty: args.qty,
            })
          }
        }
        await resolveItem.mutateAsync({
          sku: item.sku,
          action: 'confirm',
          reason: args.reason,
          recountConfirmed: args.recountConfirmed,
        })
      }
      loadReview()
    } catch (err) {
      reportFailure(err)
    } finally {
      setBusy(false)
    }
  }

  /**
   * A shortage that undercuts what customers already reserved is confirmed only after the counter
   * has seen which pre-orders it hits — from the row's check as well as from the sheet.
   */
  function requestSettle(
    item: CountItemView,
    kind: ItemKind,
    args: ConfirmArgs,
  ) {
    setSheetTarget(null)
    if (item.affectedOrders.length > 0) {
      setPendingConfirm({
        item,
        kind,
        args,
        affectedOrders: item.affectedOrders,
      })
      return
    }
    void settleItem(item, kind, args)
  }

  function ignoreItem(item: CountItemView) {
    setSheetTarget(null)
    resolveItem.mutate(
      { sku: item.sku, action: 'skip' },
      { onSuccess: loadReview, onError: reportFailure },
    )
  }

  /**
   * One request for all of them, and it reports its own outcome. This used to be N parallel
   * resolves — twenty-two outstanding articles were twenty-two POSTs, of which some could fail
   * while others landed.
   */
  function ignoreRemaining() {
    skipRemaining.mutate(undefined, {
      onSuccess: (result) => {
        toast.success(
          t('review.ignoreRemainingDone', { count: result.skipped }),
        )
        loadReview()
      },
      onError: () => toast.error(t('review.ignoreRemainingFailed')),
    })
  }

  /** Takes an applied decision back: the correction is reversed, the article is open again. */
  function reopenItem(item: CountItemView) {
    setReopenTarget(null)
    resolveItem.mutate(
      { sku: item.sku, action: 'reopen' },
      {
        onSuccess: () => {
          toast.success(t('review.reopened'))
          loadReview()
        },
        onError: reportFailure,
      },
    )
  }

  /** The pad's result comes back here and lands in the article sheet, not on the server yet. */
  function submitPadQty(qty: number) {
    const target = padTarget
    if (!target) return
    setPadTarget(null)
    if (target.kind === 'variance' && target.item.countedQty != null) {
      // A recount is a decision of its own — it replaces the counted quantity and satisfies the
      // surplus gate — so it goes to the server directly, then the sheet reopens on fresh numbers.
      resolveItem.mutate(
        { sku: target.item.sku, action: 'recount', recountQty: qty },
        {
          onSuccess: (item) => {
            setSheetTarget({ item, kind: target.kind })
            loadReview()
          },
          onError: reportFailure,
        },
      )
      return
    }
    // Nothing was captured yet: the number stays in the sheet until the counter confirms it, so a
    // mistyped quantity can still be corrected before it becomes a stock movement.
    setSheetTarget({
      item: { ...target.item, countedQty: qty },
      kind: target.kind,
    })
  }

  function proceedPendingConfirm() {
    if (!pendingConfirm) return
    const { item, kind, args } = pendingConfirm
    setPendingConfirm(null)
    void settleItem(item, kind, args)
  }

  function closeAndFinish() {
    closeSession.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('review.closed'))
        navigate({ to: '/inventur' })
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          toast.error(t('review.unresolvedItems'))
        } else {
          toast.error(t('review.closeFailed'))
        }
      },
    })
  }

  function highlight(sku: string) {
    setHighlightSku(sku)
    if (highlightTimer.current) clearTimeout(highlightTimer.current)
    highlightTimer.current = setTimeout(
      () => setHighlightSku((s) => (s === sku ? null : s)),
      HIGHLIGHT_MS,
    )
  }

  function scrollToFirstPending() {
    // Section order on screen: shortages, surpluses, then the articles never captured.
    const target =
      (data?.variances ?? [])
        .filter((item) => (item.delta ?? 0) < 0)
        .find(isPending) ??
      (data?.variances ?? [])
        .filter((item) => (item.delta ?? 0) > 0)
        .find(isPending) ??
      (data?.notCounted ?? []).find(isPending)
    if (!target) return
    highlight(target.sku)
    itemNodes.current[target.sku]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  if (!sessionId) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t('capture.noSession')}
        </p>
        <Button onClick={() => navigate({ to: '/inventur' })}>
          {t('layout.back')}
        </Button>
      </div>
    )
  }

  if (data == null) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  const matches = data.matches
  const notCounted = data.notCounted
  const unknownScans = data.unknownScans
  // A shortage and a surplus are different decisions and live in different sections; the server
  // still reports both as "variances".
  const shortages = data.variances.filter((item) => (item.delta ?? 0) < 0)
  const surpluses = data.variances.filter((item) => (item.delta ?? 0) > 0)

  const pendingShortages = shortages.filter(isPending).length
  const pendingSurpluses = surpluses.filter(isPending).length
  const pendingNotCounted = notCounted.filter(isPending).length
  const totalOpen = pendingShortages + pendingSurpluses + pendingNotCounted
  const allResolved = totalOpen === 0

  function varianceRow(item: CountItemView) {
    return (
      <CountVarianceRow
        key={item.sku}
        item={item}
        effectiveReason={sectionReason}
        onOpen={() => setSheetTarget({ item, kind: 'variance' })}
        onConfirm={(recountConfirmed) =>
          requestSettle(item, 'variance', {
            reason: sectionReason,
            recountConfirmed,
          })
        }
        onReopen={() => setReopenTarget(item)}
        highlighted={highlightSku === item.sku}
        rowRef={registerItemNode(item.sku)}
      />
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <CountSummaryBar
        matched={matches.length}
        variances={data.variances.length}
        open={notCounted.length}
      />

      {/*
        One reason for both variance sections, asked once. A count with twenty variances used to
        repeat the same five pills twenty times; once a reason is chosen the block collapses to the
        line it deserves, and a row can still override it in its own sheet.
      */}
      {data.variances.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg bg-muted/50 p-3">
          {reasonPickerOpen || sectionReason == null ? (
            <>
              <p className="text-xs font-medium">
                {t('review.sectionReasonLabel')}
              </p>
              <CountReasonPicker
                value={sectionReason}
                onChange={(reason) => {
                  setSectionReason(reason)
                  if (reason != null) setReasonPickerOpen(false)
                }}
              />
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">
                  {t('review.sectionReasonLabel')}
                </p>
                <p className="truncate text-sm font-semibold">
                  {t(`review.reason.${sectionReason}`)}
                </p>
              </div>
              <Button
                variant="outline"
                className="h-11 shrink-0 bg-background"
                onClick={() => setReasonPickerOpen(true)}
              >
                {t('review.changeReason')}
              </Button>
            </div>
          )}
        </div>
      )}

      {shortages.length > 0 && (
        <CountReviewSection
          label={t('review.sectionShortages')}
          openCount={pendingShortages}
        >
          {shortages.map(varianceRow)}
        </CountReviewSection>
      )}

      {surpluses.length > 0 && (
        <CountReviewSection
          label={t('review.sectionSurpluses')}
          openCount={pendingSurpluses}
          hint={t('review.surplusHint')}
        >
          {surpluses.map(varianceRow)}
        </CountReviewSection>
      )}

      {notCounted.length > 0 && (
        <CountReviewSection
          label={t('review.sectionNotFound')}
          openCount={pendingNotCounted}
          action={
            /*
              One request for every remaining row, and absent while there is at most one of them —
              a single row is one tap in its own sheet.
            */
            pendingNotCounted > 1 ? (
              <Button
                variant="outline"
                className="h-10 shrink-0 px-3 text-[13px]"
                disabled={skipRemaining.isPending}
                onClick={ignoreRemaining}
              >
                <X className="h-4 w-4" />
                {t('review.ignoreRemaining', { count: pendingNotCounted })}
              </Button>
            ) : undefined
          }
        >
          {notCounted.map((item) => (
            <CountNotFoundRow
              key={item.sku}
              item={item}
              onOpen={() => setSheetTarget({ item, kind: 'notCounted' })}
              onReopen={() => setReopenTarget(item)}
              highlighted={highlightSku === item.sku}
              rowRef={registerItemNode(item.sku)}
            />
          ))}
          <p className="px-4 py-3 text-xs text-muted-foreground">
            {t('review.notFoundHint')}
          </p>
        </CountReviewSection>
      )}

      {/*
        What needs no decision stays folded away: these two sections are the record of the count,
        not work to do.
      */}
      {(matches.length > 0 || unknownScans.length > 0) && (
        <Card>
          <CardContent className="p-0">
            <Accordion type="multiple">
              {matches.length > 0 && (
                <AccordionItem value="matches">
                  <AccordionTrigger className="px-4">
                    {t('review.matchesTitle', { count: matches.length })}
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-2 px-4">
                    {matches.map((item) => (
                      <div
                        key={item.sku}
                        className="flex items-center justify-between border-b border-border py-2 last:border-0"
                      >
                        <p className="text-sm">{item.name}</p>
                        <span className="text-sm tabular-nums text-muted-foreground">
                          {item.countedQty == null
                            ? null
                            : formatStockDisplay(item.countedQty)}
                        </span>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              )}

              {unknownScans.length > 0 && (
                <AccordionItem value="unknownScans">
                  <AccordionTrigger className="px-4">
                    {t('review.unknownScansTitle')}
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-2 px-4">
                    {unknownScans.map((scan, idx) => (
                      <div
                        key={`${scan.code}-${idx}`}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="font-mono">{scan.code}</span>
                        <span className="tabular-nums">{scan.qty}</span>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/*
        The footer bleeds over the layout's own bottom padding (including its safe-area inset) and
        re-adds the inset as its own padding, so its background reaches the bottom edge of the
        screen while the button itself stays clear of the home indicator.
      */}
      <div
        className="sticky bottom-0 -mx-4 border-t border-border bg-background px-4 py-3"
        style={{
          marginBottom: 'calc(-1rem - env(safe-area-inset-bottom))',
          ...safeAreaBottom('0.75rem'),
        }}
      >
        {allResolved ? (
          <Button
            size="lg"
            className="h-12 w-full"
            disabled={closeSession.isPending}
            onClick={closeAndFinish}
          >
            {t('review.close')}
          </Button>
        ) : (
          <Button
            size="lg"
            variant="outline"
            className="h-12 w-full"
            onClick={scrollToFirstPending}
          >
            {t('review.itemsOpen', { count: totalOpen })}
          </Button>
        )}
      </div>

      {/* Quantity, reason and the article's own actions, over the list. */}
      <CountItemSheet
        target={sheetTarget}
        groupReason={sectionReason}
        pending={busy}
        onClose={() => setSheetTarget(null)}
        onOpenPad={() => {
          const target = sheetTarget
          if (!target) return
          setSheetTarget(null)
          setPadTarget({ item: target.item, kind: target.kind })
        }}
        onConfirm={(args) => {
          const target = sheetTarget
          if (!target) return
          requestSettle(target.item, target.kind, args)
        }}
        onIgnore={() => {
          const target = sheetTarget
          if (target) ignoreItem(target.item)
        }}
      />

      {/* The digit pad, for a quantity that is not one or two steps away. */}
      <QuantitySheet
        target={
          padTarget
            ? {
                sku: padTarget.item.sku,
                name: padTarget.item.name,
                imageUrl: null,
                previousQty: padTarget.item.countedQty,
                ...(assortment.itemBySku(padTarget.item.sku)
                  ?.fractionalCounting === true
                  ? {
                      fractional: true,
                      unit: assortment.itemBySku(padTarget.item.sku)?.unit,
                    }
                  : {}),
              }
            : null
        }
        onClose={() => setPadTarget(null)}
        onSubmit={submitPadQty}
        mode="replace"
      />

      {/*
        Reopening moves stock, so it is the one thing here that asks first — it is the recovery
        path for a mis-tap and must not itself be mis-tappable.
      */}
      <Drawer
        open={reopenTarget != null}
        onOpenChange={(open) => !open && setReopenTarget(null)}
      >
        <DrawerContent className="max-h-[90svh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{reopenTarget?.name}</DrawerTitle>
            <DrawerDescription>
              {/*
                Only a confirm moved stock: a match and an ignore never wrote a movement, so
                reopening them changes nothing but the row's state, and saying otherwise would
                worry the counter for nothing.
              */}
              {reopenTarget != null && movedStock(reopenTarget)
                ? t('review.reopenBody', {
                    amount: formatStockDisplay(bookedAmount(reopenTarget)),
                  })
                : t('review.reopenBodyNoStock')}
            </DrawerDescription>
          </DrawerHeader>
          <DrawerFooter style={safeAreaBottom('1rem')}>
            <Button
              size="lg"
              className="h-12"
              disabled={resolveItem.isPending}
              onClick={() => reopenTarget && reopenItem(reopenTarget)}
            >
              {t('review.reopenConfirm')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-12"
              onClick={() => setReopenTarget(null)}
            >
              {t('quantitySheet.cancel')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/*
        A drawer rather than a centred AlertDialog: this warning is read and acted on with a
        thumb, on the same phone as the rest of the flow.
      */}
      <Drawer
        open={pendingConfirm != null}
        onOpenChange={(open) => !open && setPendingConfirm(null)}
      >
        <DrawerContent className="max-h-[90svh]">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" aria-hidden />
              {t('review.preOrderWarningTitle')}
            </DrawerTitle>
            <DrawerDescription>
              {t('review.preOrderWarningBody')}
            </DrawerDescription>
          </DrawerHeader>
          <ul className="flex min-h-0 flex-col gap-1 overflow-y-auto px-4 pb-2 text-center">
            {pendingConfirm?.affectedOrders.map((o) => (
              <li key={o.orderId} className="text-sm">
                {o.pickupCode ?? o.orderId}
                {o.pickupAt
                  ? ` — ${new Date(o.pickupAt).toLocaleString()}`
                  : ''}
              </li>
            ))}
          </ul>
          <DrawerFooter style={safeAreaBottom('1rem')}>
            <Button
              variant="destructive"
              size="lg"
              className="h-12"
              onClick={proceedPendingConfirm}
            >
              {t('review.preOrderConfirm')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-12"
              onClick={() => setPendingConfirm(null)}
            >
              {t('review.preOrderCancel')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
